import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import React, { useCallback, useMemo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import _ from 'lodash';
import { createDateFromYYYYMMFormat, parseFormikErrors } from '../../utils';
import { PageWrapper } from '../../components';
import { CustomIcon, IconName } from '../../components/customIcon/CustomIcon';
import { OperationalCost } from '../../types';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers';
import {
  getDateFilterValueLocal,
  saveOperationalCost,
} from '../../redux/accounts';

export interface OperationalCostFormik {
  operationalCost: number | null;
  period: number;
}

interface Props {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  operationalCostList: OperationalCost[] | undefined;
}

const EditOperationalCostModal: React.FC<Props> = ({
  isModalOpen,
  handleCloseModal,
  operationalCostList,
}) => {
  const dispatch = useAppDispatch();
  const dateFilterValueLocal = useAppSelector(getDateFilterValueLocal);

  const initialValues: OperationalCostFormik = useMemo(() => {
    const currentPeriod = dateFilterValueLocal
      ? Number(DateTime.fromISO(dateFilterValueLocal).toFormat('yyyyMM'))
      : Number(DateTime.now().toFormat('yyyyMM'));

    const operationalCost = operationalCostList
      ? [...operationalCostList]
          .sort((a, b) => b.period - a.period)
          ?.find((data) => data.period <= currentPeriod)
      : {
          period: currentPeriod,
          operationalCost: 11,
        };
    return (
      operationalCost ?? {
        operationalCost: null,
        period: currentPeriod,
      }
    );
  }, [operationalCostList, dateFilterValueLocal]);

  const formik = useFormik<OperationalCostFormik>({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      operationalCost: yup.number().required('Value is required'),
      period: yup.number().required('Value is required'),
    }),
    onSubmit: (values) => {
      if (!values.operationalCost) return;

      dispatch(
        saveOperationalCost({
          operationalCost: Number(values.operationalCost),
          period: values.period,
        }),
      );
      resetForm();
    },
    validateOnMount: false,
    validateOnChange: false,
  });

  const handleOperationCostChange = useCallback(
    (value: string, field: string) => {
      if (field === 'period') {
        const operationalCostForSelectedPeriod =
          operationalCostList &&
          [...operationalCostList]
            .sort((a, b) => b.period - a.period)
            ?.find(
              (data) =>
                data.period <=
                (value
                  ? Number(value)
                  : Number(DateTime.now().toFormat('yyyyMM'))),
            );
        formik.setFieldValue(
          'operationalCost',
          operationalCostForSelectedPeriod?.operationalCost || '',
        );
      }
      formik.setFieldValue(field, value);
    },
    [formik, operationalCostList],
  );

  const activeSinceLabel = useMemo(() => {
    if (!operationalCostList?.length || !formik.values.period) return null;

    const active = [...operationalCostList]
      .sort((a, b) => b.period - a.period)
      .find((data) => data.period <= formik.values.period);

    if (!active) return null;

    return DateTime.fromFormat(String(active.period), 'yyyyMM').toFormat(
      'MMMM yyyy',
    );
  }, [operationalCostList, formik.values.period]);

  const resetForm = useCallback(() => {
    formik.resetForm();
    handleCloseModal();
  }, [formik, handleCloseModal]);

  return (
    <Dialog
      fullWidth
      onClose={handleCloseModal}
      open={isModalOpen}
      scroll="body"
      maxWidth="sm"
    >
      <DialogTitle>Operational cost</DialogTitle>
      <CloseOutlinedIcon
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 22,
          top: 21,
          color: 'grey',
          cursor: 'pointer',
        }}
        onClick={resetForm}
        fontSize="small"
      ></CloseOutlinedIcon>

      <DialogContent sx={{ maxHeight: 600 }}>
        <PageWrapper sx={{ paddingBlock: 0 }}>
          <Box
            sx={{ mb: 3, bgcolor: 'highlight.neutral', p: 2, borderRadius: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CustomIcon
                icon={IconName.Info}
                sx={{ color: 'text.secondary' }}
              />
              <Box>
                <Typography variant="body1" color="text.secondary">
                  Only the Finance team can set the operational cost.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" gap={3} alignItems={'center'}>
            <TextField
              id="operationalCost"
              type="number"
              sx={{
                width: '40%',
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              label="Operational cost"
              placeholder="Add operational cost"
              value={formik.values.operationalCost}
              name={`operationalCost`}
              error={Boolean(formik.errors.operationalCost)}
              onChange={(e) =>
                handleOperationCostChange(e.target.value, 'operationalCost')
              }
              InputProps={{
                startAdornment: (
                  <Typography
                    sx={{
                      color: 'text.disabled',
                      paddingInlineEnd: 0.7,
                      paddingTop: 0.17,
                    }}
                  >
                    $
                  </Typography>
                ),
                size: 'small',
                endAdornment: (
                  <Typography sx={{ color: 'text.disabled' }}>/h</Typography>
                ),
              }}
            />
            <DatePicker
              views={['year', 'month']}
              onChange={(date: Date | null) =>
                handleOperationCostChange(
                  DateTime.fromJSDate(date || new Date()).toFormat('yyyyMM'),
                  'period',
                )
              }
              value={createDateFromYYYYMMFormat(formik.values.period)}
              format="MM/yyyy"
              label="Period"
              slotProps={{
                textField: {
                  error: Boolean(formik.errors.period),
                  size: 'small',
                },
                popper: {
                  placement: 'top',
                },
              }}
            />
            <Box>
              <Typography variant="body1" color="text.secondary">
                Fixed globally for accounts.
              </Typography>
            </Box>
          </Box>

          {formik.errors.operationalCost && (
            <Box>
              <>
                {parseFormikErrors(formik.errors.operationalCost).map(
                  (error) => (
                    <Typography key={error} color="error">
                      {error}
                    </Typography>
                  ),
                )}
              </>
            </Box>
          )}

          {activeSinceLabel && (
            <Typography variant="caption" color="text.secondary" mt={0.5}>
              Active since {activeSinceLabel}
            </Typography>
          )}
        </PageWrapper>
      </DialogContent>

      <DialogActions
        sx={{ alignItems: 'stretch', justifyContent: 'space-between' }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          justifyContent="space-between"
          gap={1}
        >
          <Button onClick={resetForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={formik.submitForm}
            disabled={!formik.dirty}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditOperationalCostModal;
export {};
